import { Store, Gem, Shirt, Smartphone, Utensils, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useStaticQuery } from "@/hooks/useOptimizedQuery";
import { cacheKeys } from "@/lib/cache";

const categories = [
  { name: "Fashion", icon: <Shirt size={24} className="text-foreground/70" />, hasSubcategories: true },
  { name: "Electronics", icon: <Smartphone size={24} className="text-foreground/70" /> },
  { name: "Jewelry", icon: <Gem size={24} className="text-foreground/70" /> },
  { name: "Beauty Products", icon: <Sparkles size={24} className="text-foreground/70" /> },
  { name: "Food & Drink", icon: <Utensils size={24} className="text-foreground/70" /> },
  { name: "Local Stores", icon: <Store size={24} className="text-foreground/70" /> },
];

const toSlug = (name: string) => name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and");

const CategoryGrid = () => {
  const navigate = useNavigate();

  // Use optimized static query for categories (rarely change)
  const { data, isLoading, cacheHit } = useStaticQuery(
    cacheKeys.categories,
    async () => {
      console.log('🔄 Fetching categories from database...');
      const { data } = await supabase.from("categories").select("slug, name").order("name");
      return data ?? [];
    },
    {
      // Categories are very static, cache for longer
      trackPerformance: true,
    }
  );

  // Show cache hit status in development
  if (import.meta.env.DEV && cacheHit) {
    console.log('⚡ Categories loaded from cache');
  }

  const list = (data?.length
    ? data.map((c: any) => ({ name: c.name, slug: c.slug }))
    : categories.map((c) => ({ name: c.name, slug: toSlug(c.name), hasSubcategories: c.hasSubcategories })));

  const iconFor = (name: string) => {
    const key = name.toLowerCase();
    if (key.includes("fashion")) return <Shirt size={24} className="text-foreground/70" />;
    if (key.includes("food") || key.includes("gourmet") || key.includes("drink")) return <Utensils size={24} className="text-foreground/70" />;
    if (key.includes("beauty") || key.includes("cosmetic")) return <Sparkles size={24} className="text-foreground/70" />;
    if (key.includes("jewelry") || key.includes("jewellery")) return <Gem size={24} className="text-foreground/70" />;
    if (key.includes("electronic")) return <Smartphone size={24} className="text-foreground/70" />;
    return <Store size={24} className="text-foreground/70" />;
  };

  if (isLoading) {
    return (
      <section className="space-y-8">
        <div className="text-center space-y-3">
          <div className="h-8 bg-muted rounded w-64 mx-auto animate-pulse"></div>
          <div className="h-6 bg-muted rounded w-96 mx-auto animate-pulse"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="p-3 rounded-full bg-muted mx-auto w-12 h-12 mb-3"></div>
                <div className="h-4 bg-muted rounded w-20 mx-auto mb-1"></div>
                <div className="h-3 bg-muted rounded w-16 mx-auto"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          Browse by Category
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Discover premium products across our carefully curated collections
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {list.map((cat) => (
          <div
            key={cat.slug}
            className="group flex flex-col items-center gap-3 bg-card hover:bg-muted/30 rounded-lg transition-all duration-300 p-6 cursor-pointer border border-border/40 hover:border-border hover:shadow-luxury-md"
            onClick={() => navigate(`/category/${cat.slug}`)}
          >
            <div className="p-3 rounded-full bg-muted/50 group-hover:bg-muted transition-colors duration-300">
              {iconFor(cat.name)}
            </div>
            <div className="text-center space-y-1">
              <span className="font-medium text-sm text-foreground group-hover:text-foreground/90 transition-colors duration-300">
                {cat.name}
              </span>
              {cat.hasSubcategories && (
                <span className="text-xs text-muted-foreground">Men • Women • Kids</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CategoryGrid;